import React, { useState, useMemo } from "react";
import { useGetGpuListQuery, useDeleteGpuMutation } from "../store/api/gpuApi";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "../components/ui/pagination";
import {
  Pencil,
  Trash2,
  Plus,
  Filter,
  ArrowUpDown,
  Download,
} from "lucide-react";
import { Button } from "../components/ui/button";
import GpuFormModal from "../components/modals/GpuFormModal";
import ConfirmModal from "../components/modals/ConfirmModal";
import SearchInput from "../components/SearchInput";
import { Loader, InlineLoader } from "../components/Loader";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const GpuManagement = () => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [formMode, setFormMode] = useState("create");
  const [sortBy, setSortBy] = useState("gpuname");
  const [sortOrder, setSortOrder] = useState("asc");
  const [filterGpuType, setFilterGpuType] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const limit = 10;

  const { data, isLoading, error } = useGetGpuListQuery({
    limit: 100, // Fetch more for client-side sorting/filtering
    offset: 0,
    search,
  });

  const [deleteGpu, { isLoading: isDeleting }] = useDeleteGpuMutation();

  // Client-side filtering and sorting
  const processedData = useMemo(() => {
    if (!data?.data) return [];

    let filtered = [...data.data];

    // Apply GPU Type filter
    if (filterGpuType !== "all") {
      filtered = filtered.filter((item) => item.gpu_type === filterGpuType);
    }

    // Apply Status filter
    if (filterStatus !== "all") {
      filtered = filtered.filter((item) => {
        if (filterStatus === "available") return Number(item.gpu_available) > 0;
        if (filterStatus === "full") return Number(item.gpu_available) === 0;
        if (filterStatus === "low")
          return (
            Number(item.gpu_available) > 0 &&
            Number(item.gpu_available) <= Number(item.gpu_installed) * 0.2
          );
        return true;
      });
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aVal = a[sortBy];
      let bVal = b[sortBy];

      if (typeof aVal === "string") {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      } else {
        aVal = Number(aVal) || 0;
        bVal = Number(bVal) || 0;
      }

      if (aVal < bVal) return sortOrder === "asc" ? -1 : 1;
      if (aVal > bVal) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [data?.data, sortBy, sortOrder, filterGpuType, filterStatus]);

  // Paginate processed data
  const paginatedData = useMemo(() => {
    const start = (page - 1) * limit;
    return processedData.slice(start, start + limit);
  }, [processedData, page]);

  const totalPages = Math.ceil(processedData.length / limit);

  // Get unique GPU types for filter
  const uniqueGpuTypes = useMemo(() => {
    if (!data?.data) return [];
    return [...new Set(data.data.map((item) => item.gpu_type))].sort();
  }, [data?.data]);

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handleCreate = () => {
    setSelectedId(null);
    setFormMode("create");
    setFormModalOpen(true);
  };

  const handleEdit = (id) => {
    setSelectedId(id);
    setFormMode("edit");
    setFormModalOpen(true);
  };

  const handleDelete = (id) => {
    setSelectedId(id);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await deleteGpu(selectedId).unwrap();
      setDeleteDialogOpen(false);
    } catch (error) {
      console.error("Failed to delete GPU:", error);
    }
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
  };

  const handleExport = () => {
    const csv = [
      [
        "Instance Type",
        "GPU Name",
        "GPU Card",
        "GPU Type",
        "Committed",
        "Installed",
        "Provided",
        "Allocated",
        "Utilised",
        "Available",
      ].join(","),
      ...paginatedData.map((item) =>
        [
          item.instance_type,
          item.gpuname,
          item.gpu_card,
          item.gpu_type,
          item.gpu_committed,
          item.gpu_installed,
          item.gpu_provided,
          item.gpu_allocated,
          item.gpu_utilised,
          item.gpu_available,
        ].join(","),
      ),
    ].join("\n");

    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "gpu-management.csv";
    a.click();
  };

  const SortableHeader = ({ field, label }) => (
    <TableHead
      className="cursor-pointer hover:bg-gray-100 select-none"
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center gap-2">
        {label}
        {sortBy === field && <ArrowUpDown className="h-3 w-3" />}
      </div>
    </TableHead>
  );

  return (
    <>
      <div className="space-y-4">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold">GPU Management</h1>
            <p className="text-sm text-gray-500 mt-1">
              Total: {processedData.length} GPU
              {processedData.length !== 1 ? "s" : ""}
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <Button
              onClick={handleExport}
              variant="outline"
              className="flex items-center gap-2"
            >
              <Download className="h-4 w-4" />
              <span className="hidden sm:inline">Export</span>
            </Button>
            <Button onClick={handleCreate} className="flex items-center gap-2">
              <Plus className="h-4 w-4" />
              <span>Add New</span>
            </Button>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="flex flex-col md:flex-row gap-3 bg-white p-4 rounded-lg border">
          <div className="flex-1">
            <SearchInput search={search} handleSearch={handleSearch} />
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <Select value={filterGpuType} onValueChange={setFilterGpuType}>
              <SelectTrigger className="w-full sm:w-48">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="GPU Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {uniqueGpuTypes.map((type) => (
                  <SelectItem key={type} value={type}>
                    {type}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-full sm:w-40">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="available">Available</SelectItem>
                <SelectItem value="low">Low (≤20%)</SelectItem>
                <SelectItem value="full">Full</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Table */}
        <div className="border rounded-lg overflow-x-auto">
          {isLoading ? (
            <div className="p-8">
              <Loader size="md" label="Loading GPU data..." />
            </div>
          ) : error ? (
            <div className="p-8 text-center text-red-500">
              <p>Error loading data. Please try again.</p>
            </div>
          ) : processedData.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <p>No GPU data found</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableHeader field="instance_type" label="Instance Type" />
                  <SortableHeader field="gpuname" label="GPU Name" />
                  <TableHead>GPU Card</TableHead>
                  <SortableHeader field="gpu_type" label="GPU Type" />
                  <SortableHeader field="gpu_committed" label="Committed" />
                  <SortableHeader field="gpu_installed" label="Installed" />
                  <SortableHeader field="gpu_provided" label="Provided" />
                  <SortableHeader field="gpu_allocated" label="Allocated" />
                  <SortableHeader field="gpu_utilised" label="Utilised" />
                  <SortableHeader field="gpu_available" label="Available" />
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedData.map((item) => (
                  <TableRow key={item.id} className="hover:bg-gray-50">
                    <TableCell className="font-medium">
                      {item.instance_type}
                    </TableCell>
                    <TableCell className="truncate max-w-xs">
                      {item.gpuname}
                    </TableCell>
                    <TableCell>{item.gpu_card}</TableCell>
                    <TableCell>{item.gpu_type}</TableCell>
                    <TableCell>{item.gpu_committed}</TableCell>
                    <TableCell>{item.gpu_installed}</TableCell>
                    <TableCell>{item.gpu_provided}</TableCell>
                    <TableCell>{item.gpu_allocated}</TableCell>
                    <TableCell>{item.gpu_utilised}</TableCell>
                    <TableCell>
                      <span
                        className={
                          item.gpu_available > 0
                            ? "text-green-600 font-medium"
                            : "text-red-600 font-medium"
                        }
                      >
                        {item.gpu_available}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-4 w-4"
                          onClick={() => handleEdit(item.id)}
                          title="Edit"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-4 w-4 text-red-500 hover:text-red-500"
                          onClick={() => handleDelete(item.id)}
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && !isLoading && (
          <div className="flex justify-center">
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                  />
                </PaginationItem>
                {[...Array(totalPages)].map((_, i) => (
                  <PaginationItem key={i + 1}>
                    <PaginationLink
                      onClick={() => setPage(i + 1)}
                      isActive={page === i + 1}
                    >
                      {i + 1}
                    </PaginationLink>
                  </PaginationItem>
                ))}
                <PaginationItem>
                  <PaginationNext
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        )}
      </div>

      <GpuFormModal
        isOpen={formModalOpen}
        onClose={() => setFormModalOpen(false)}
        id={selectedId}
        mode={formMode}
      />

      {/* Delete Confirm Modal */}
      <ConfirmModal
        isOpen={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={confirmDelete}
        title="Delete GPU"
        isDeleting={isDeleting}
        description="Are you sure you want to delete this GPU?"
      />
    </>
  );
};

export default GpuManagement;
